import { useMemo } from 'react'
import { useQuery } from 'apollo-client'
import dayjs from 'dayjs'
import { useFt } from 'hooks'
import { constants } from 'helpers'

import limitedDropSaleQuery from './graph/limitedDropSale.graphql'
import limitedDropSaleCurrentQuery from './graph/limitedDropSaleCurrent.graphql'


type UseLimitedDropSaleProps = {
  id?: string
  detailed?: boolean
  skip?: boolean
}

const analyticEventStatusMap = {
  LIVE: 'Active',
  SOLD_OUT: 'Sold out',
  FINISHED: 'Ended',
}

const useLimitedDropSale = (props?: UseLimitedDropSaleProps) => {
  const { id, detailed = false, skip } = props || {}
  const isExactLimitedDropSale = Boolean(id)
  const isLimitedDropEnabled = useFt(constants.features.limitedDrops)

  const { data: exactSaleData, isFetching: isExactSaleFetching, error: exactSaleError } = useQuery(limitedDropSaleQuery, {
    fetchPolicy: 'cache-first',
    skip: skip || !isExactLimitedDropSale || !isLimitedDropEnabled,
    variables: {
      input: {
        uid: id,
      },
      detailed,
    },
  })
  const { data: currentSaleData, isFetching: isCurrentSaleFetching, error: currentSaleError } = useQuery(limitedDropSaleCurrentQuery, {
    fetchPolicy: 'cache-first',
    skip: skip || isExactLimitedDropSale || !isLimitedDropEnabled,
    variables: {
      detailed,
    },
  })

  const data = exactSaleData?.limitedDropSale?.data || currentSaleData?.limitedDropSaleCurrent?.data

  const modifiedData = useMemo<LimitedDropSale.Payload>(() => {
    if (!data) {
      return null
    }

    const now = dayjs()

    const expirationTime = data.endDate ? dayjs(data.endDate).diff(now) : null
    const isPreSale = now.isBefore(data.startDate)
    const isSoldOut = data.status === 'SOLD_OUT' || data.status === 'FINISHED' || expirationTime <= 0

    const metadata: LimitedDropSale.Metadata = { productIds: [], productBrands: [], productFullNames: [], limitedDropStatus: analyticEventStatusMap[data.status] }

    data.products?.forEach((product) => {
      const { id, brandInfo, fullName } = product.productInfo
      const isPartiallySoldOut = !isSoldOut && product.limitedEditionInfo?.productsRemainingCount === 0

      metadata.productIds.push(id)
      metadata.productBrands.push(brandInfo.name)
      metadata.productFullNames.push(fullName)

      metadata.limitedDropStatus = isPartiallySoldOut ? 'Partially sold-out' : metadata.limitedDropStatus
    })

    return {
      ...data,
      isPreSale,
      isSoldOut,
      metadata,
      expirationTime,
      isCurrentSale: !isExactLimitedDropSale,
    }
  }, [ data, isExactLimitedDropSale ])

  return {
    data: modifiedData,
    error: exactSaleError || currentSaleError,
    isFetching: isExactSaleFetching || isCurrentSaleFetching,
  }
}

export default useLimitedDropSale
